<script setup>
    // Component
    import BaseInput from '@/components/BaseInputComponent.vue';
    import MainHeader from '@/components/HeaderComponent.vue';

    // Store
    import { useLoginStore } from '@/stores/login/loginStore';
    import { useSetupParentStore } from '@/stores/setup/setupParentStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Utils
    import Utils from '@/commonProject/helpers/utils.js';
    import i18n from '@/common/i18n';

    // Vue
    import { getCurrentInstance, inject, onMounted, reactive, ref } from 'vue';
    import { useRouter } from 'vue-router';

    // ----------------------------
    // 定数
    // ----------------------------
    const app = getCurrentInstance();

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    const loginStore = useLoginStore();

    const setupParentStore = useSetupParentStore();

    const router = useRouter();

    let disabledFlag = ref(true);

    // 同意する/同意しないラジオボタンの初期値。初期値は両ラジオボタンとも選択されていない状態にする。
    let agreeCheck = null;

    const body = {
        ParentName: '',
        ParentRelation: '',
        ParentTel: '',
        ParentMail: '',
    };

    const setupForm = reactive(body);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    onMounted(async () => {});

    /**
     * MFA暗唱コード入力
     */
    async function checkForm() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }

        if (agreeCheck !== 'agree') {
            return;
        }

        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        const response = await setupParentStore.setupUserParentAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S, setupForm);

        if (response.success) {
            // ホーム画面へ遷移する。
            router.push({ name: 'home' });
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }
    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // 法定代理人の氏名
        if (Utils.isNullEmpty(setupForm.ParentName)) {
            message += i18n.global.t('setupParent.message.message1') + '\n';
        } else {
            if (Utils.checkLengthOfShortString(setupForm.ParentName)) {
                message += i18n.global.t('setupParent.message.message2') + '\n';
            }
        }
        // 法定代理人の続柄
        if (Utils.isNullEmpty(setupForm.ParentRelation)) {
            message += i18n.global.t('setupParent.message.message3') + '\n';
        } else {
            if (Utils.checkLengthOfShortString(setupForm.ParentRelation)) {
                message += i18n.global.t('setupParent.message.message4') + '\n';
            }
        }
        // 法定代理人の電話番号
        if (Utils.isNullEmpty(setupForm.ParentTel)) {
            message += i18n.global.t('setupParent.message.message5') + '\n';
        } else {
            if (Utils.checkLengthOfTelNumber(setupForm.ParentTel)) {
                message += i18n.global.t('setupParent.message.message6') + '\n';
            }
            if (!Utils.isMatchPatternTelNumber(setupForm.ParentTel)) {
                message += i18n.global.t('setupParent.message.message7') + '\n';
            }
        }
        // 法定代理人のメールアドレス
        if (Utils.isNullEmpty(setupForm.ParentMail)) {
            message += i18n.global.t('setupParent.message.message8') + '\n';
        } else {
            if (Utils.checkLengthOfMailAddress(setupForm.ParentMail)) {
                message += i18n.global.t('setupParent.message.message9') + '\n';
            }
            if (!Utils.isMatchPatternMailAddress(setupForm.ParentMail)) {
                message += i18n.global.t('setupParent.message.message10') + '\n';
            }
        }
        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    function agreeChange() {
        if (agreeCheck === 'agree') {
            disabledFlag.value = false;
        } else {
            disabledFlag.value = true;
        }
    }

    function cancel() {
        // ログイン画面へ遷移する。
        router.push({ name: 'login', query: { vid: loginStore.getVendorId } });
    }
</script>

<style></style>

<template>
    <section id="main" class="bg">
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3>
                    <span>{{ $t('setupParent.parts.pageTitle1') }}</span>
                </h3>
                <section class="whiteBoxContents">
                    <p class="pageInfo">{{ $t('setupParent.parts.pageInformation1') }}</p>
                    <section class="form">
                        <dl>
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('setupParent.parts.dl1') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput spKeepFlex">
                                    <li>
                                        <BaseInput v-model:input="setupForm.ParentName" type="text" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('setupParent.parts.dl2') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <BaseInput v-model:input="setupForm.ParentRelation" type="text" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('setupParent.parts.dl3') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <BaseInput v-model:input="setupForm.ParentTel" type="text" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('setupParent.parts.dl4') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <BaseInput v-model:input="setupForm.ParentMail" type="email" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('setupParent.parts.dl5') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li style="width: auto; margin-right: 10px">
                                        <input type="radio" id="radio1-1" class="radio" name="radioGroup1" value="agree" v-model="agreeCheck" @change="agreeChange" />
                                        <label class="radio label" for="radio1-1">
                                            <span>{{ $t('setupParent.parts.radioLabel1') }}</span>
                                        </label>
                                    </li>
                                    <li style="width: auto">
                                        <input type="radio" id="radio1-2" class="radio" name="radioGroup1" value="disagree" v-model="agreeCheck" @change="agreeChange" />
                                        <label class="radio label" for="radio1-2">
                                            <span>{{ $t('setupParent.parts.radioLabel2') }}</span>
                                        </label>
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                    </section>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <button type="button" class="btnNormal" @click="cancel">
                                {{ $t('common.parts.cancel') }}
                            </button>
                        </li>
                        <li>
                            <button type="button" class="btnPrimary" @click="checkForm" :disabled="disabledFlag || disabledStore.isDisabled">
                                {{ $t('common.parts.entry') }}
                            </button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>
